import { lazy, Suspense, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Spin } from "antd";

import { getCountryCodeList, getHome, getOffers } from "./APIs/api";
import {
  addCountryList,
  addCustomerData,
  addOffers,
} from "./redux/slices/customerDetails";

import customerService from "./utils/customer-service";
import { AuthProvider } from "./utils/AuthProvider";
import { ProtectedRoute, PublicRoute } from "./utils/RoutesGuard";
import { MAINTENANCE_MODE } from "./utils/constants";

import FallbackUI from "./components/FallbackUI";
import MaintenanceMode from "./components/MaintenanceMode";
import useIsMobile from "./hooks/useIsMobile";
import { useNetworkStatus } from "./hooks/useNetworkStatus";

import "./App.css";
// import NewThemeBG from "./assets/new-theme.png"

// import Login from "./pages/login";
import LoginTwo from "./pages/login2";
import LoginPage from "./pages/loginPage";
import SignUp from "./pages/signUp";
import ValiramCard from "./pages/valiram-card";
import BottomNav from "./components/BottomNav";
import ForgotPassword from "./pages/forgotPassword";
import UpdatePassword from "./pages/updatePassword";

//lazy loaded components
const SignUpThree = lazy(() => import("./pages/signUp3"));
const SignUpTwo = lazy(() => import("./pages/signup2"));
const TermsAndCondition = lazy(() => import("./pages/T&C"));
const ReferralPage = lazy(() => import("./pages/referral-page"));
const HelloScreen = lazy(() => import("./pages/hello-screen"));

const MembershipScan = lazy(() => import("./pages/membership-scan"));
const SideMenu = lazy(() => import("./pages/side-menu"));
const ProfileUpdate = lazy(() => import("./pages/profile-update"));
const PointsHistory = lazy(() => import("./pages/points-history"));
const RewardsList = lazy(() => import("./pages/rewards-list"));
const RewardsCatalog = lazy(() => import("./pages/rewards-catalog"));
const GiftCard = lazy(() => import("./pages/gift-card"));
const MyFavourites = lazy(() => import("./pages/my-favourites"));
const MyGames = lazy(() => import("./pages/my-games"));
const Game = lazy(() => import("./pages/game"));
const TierInformation = lazy(() => import("./pages/tier-information"));
const FAQ = lazy(() => import("./pages/faq"));
const Brands = lazy(() => import("./pages/brands"));
const BrandPage = lazy(() => import("./pages/brand-page"));
const RewardDetails = lazy(() => import("./pages/rewards-details"));
const BrandsStoreListing = lazy(() => import("./pages/brands-store-listing"));
const StorePage = lazy(() => import("./pages/store-page"));
const YourOffers = lazy(() => import("./pages/your-offers"));
const ReferAFriend = lazy(() => import("./pages/refer-a-friend"));
const CheckIn = lazy(() => import("./pages/check-in"));
const CustomerSupport = lazy(() => import("./pages/customer-support"));
const CustomerFeedbackForm = lazy(() => import("./pages/customer-feedback-from"));
const Notifications = lazy(() => import("./pages/notifications"));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="loading-div">
    <Spin size="large" />
  </div>
);

function App() {
  const customerData = useSelector(
    (store) => store.customerDetails.customerData
  );
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [showNav, setShowNav] = useState(true);
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [category, setCategory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storeIds, setStoreIds] = useState([]);
  const [storeImages, setStoreImages] = useState([]);
  const [storeTitle, setStoreTitle] = useState({});

  const [brand, setBrand] = useState({});
  const [store, setStore] = useState([]);

  const isOnline = useNetworkStatus();

  useEffect(() => {
    const initializeApp = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchCountries(),
          fetchCustomerData(),
          fetchHomeData(),
          fetchOffers(),
        ]);
      } catch (error) {
        toast.error("Failed to initialize app data");
        console.error("App initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []); // Empty dependency array since dispatch is stable

  // API functions
  const fetchCountries = async () => {
    try {
      const response = await getCountryCodeList();
      if (response?.data?.[0]?.attributes?.country?.country_details) {
        const countries = response.data[0].attributes.country.country_details;
        const sortedCountries = countries.sort((a, b) => a.order - b.order);
        dispatch(addCountryList(sortedCountries));
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Failed to fetch country list");
    }
  };

  const fetchCustomerData = async () => {
    if (Object.keys(customerData).length === 0) {
      try {
        const response = await customerService.getCustomer();
        if (response?.status?.success === "true") {
          dispatch(addCustomerData(response?.customers?.customer[0]));
        } else if (
          response?.status?.success === "false" &&
          response?.status?.message === "All requests have failed due to errors"
        ) {
          handleSessionExpired();
        }
      } catch (error) {
        console.error("Error fetching customer data:", error);
        toast.error("Failed to fetch customer data");
      }
    }
  };

  const fetchHomeData = async () => {
    try {
      const response = await getHome();
      const data = response?.data?.[0]?.attributes?.data;
      if (data?.list) {
        const brandsData = data.list.find((item) => item.title.toLowerCase() === "brands");
        const categoriesData = data.list.find((item) => item.title.toLowerCase() === "categories");
        const StoreData = data.list.find((item) => item.title.toLowerCase() === "stores near you");
        setStores(StoreData?.sub_items);
        setBrands(brandsData?.sub_items);
        setCategory(categoriesData?.sub_items);
      }
    } catch (error) {
      console.error("Error fetching home data:", error);
      toast.error("Failed to fetch home data");
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await getOffers();
      const offers = response?.data?.[0]?.attributes?.data;
      if (offers) {
        dispatch(addOffers(offers));
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
      toast.error("Failed to fetch offers");
    }
  };

  const handleSessionExpired = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("sessionId");
    window.location.reload();
  };

  useEffect(() => {
    if (!isOnline) {
      toast.warn(
        <div
          style={{
            borderBottom: "2px solid #FC9F00", // Add border to the text
            paddingBottom: "8px",
            marginLeft: "8px",
            marginRight: "10px",
            fontWeight: 500
          }}
        >
          Internet connection not available. Please check your network.
        </div>,
        {
          icon: (
            <div
              style={{
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                width: "24px",
                height: "20px",
                backgroundColor: "#FC9F00",
                color: "white",
                borderRadius: "50%",
                fontSize: "14px",
                fontWeight: "bold",
                position: "relative",
                top: "-12px"
              }}
            >
              !
            </div>
          ), // Optional icon
          position: "bottom-center",
          style: {
            background: "rgba(0, 0, 0, 0.8)", // Dark semi-transparent background
            color: "white", // Bright text color for contrast
            borderRadius: "8px",
            padding: "10px", // Add padding for spacing
            fontSize: "14px",
            fontWeight: "bold", // Make text more visible
            textAlign: "center", // Center text
            marginBottom: "20px",
          },
          progressStyle: {
            background: "#FC9F00",
            height: "2px",
          },
          hideProgressBar: true,
          closeButton: ({ closeToast }) => (
            <span
              onClick={closeToast} // Ensure this closes the toast
              style={{
                color: "#ffffff",
                fontSize: "18px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
              }}
            >
             x
            </span>
          ),
          autoClose: false,
        }
      );
    }
  }, [isOnline]);

  if (!isMobile) {
    return <FallbackUI />;
  }

  // Check if maintenance mode is enabled
  if (MAINTENANCE_MODE.enabled) {
    return <MaintenanceMode />;
  }

  if (loading)
    return (
      <div className="loading-div">
        <Spin size="large" />
      </div>
    );
  return (
    <div
      className="App"
      //  style={{backgroundImage: `url(${NewThemeBG})`}}
    >
      <AuthProvider>
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* public routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LoginTwo setShowNav={setShowNav} />
              </PublicRoute>
            }
          />
          {/* <Route
              path="/login2"
              element={
                <PublicRoute>
                  <LoginTwo setShowNav={setShowNav} />
                </PublicRoute>
              }
            /> */}
          <Route
            path="/loginpage"
            element={
              <PublicRoute>
                <LoginPage setShowNav={setShowNav} />
              </PublicRoute>
            }
          />
          <Route
            path="/forgotpassword"
            element={
              <PublicRoute>
                <ForgotPassword setShowNav={setShowNav} />
              </PublicRoute>
            }
          />
          <Route
            path="/updatepassword"
            element={
              <PublicRoute>
                <UpdatePassword setShowNav={setShowNav} />
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <SignUp setShowNav={setShowNav} />
              </PublicRoute>
            }
          ></Route>
          <Route
            path="/signup2"
            element={
              <PublicRoute>
                <SignUpTwo setShowNav={setShowNav} />
              </PublicRoute>
            }
          />
          <Route
            path="/signup3"
            element={
              <PublicRoute>
                <SignUpThree setShowNav={setShowNav} />
              </PublicRoute>
            }
          />
          <Route
            path="/terms&condition"
            element={
              <PublicRoute>
                <TermsAndCondition setShowNav={setShowNav} />
              </PublicRoute>
            }
          ></Route>
          <Route
            path="/referralpage"
            element={
              <PublicRoute>
                <ReferralPage setShowNav={setShowNav} />
              </PublicRoute>
            }
          ></Route>

          {/* protected routes */}
          <Route
            path="/faq"
            element={
              <ProtectedRoute>
                <FAQ setShowNav={setShowNav} />
              </ProtectedRoute>
            }
          ></Route>
          <Route
            path="/hello-screen"
            element={
              <ProtectedRoute>
                <HelloScreen setShowNav={setShowNav} />
              </ProtectedRoute>
            }
          ></Route>
          <Route
            path="/valiram-card"
            element={
              <ProtectedRoute>
                <ValiramCard
                  setShowNav={setShowNav}
                  stores={stores}
                  brands={brands}
                  category={category}
                  setBrand={setBrand}
                  setStoreIds={setStoreIds}
                  setStoreTitle={setStoreTitle}
                  setStores={setStores}
                  // setStore={setStore}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/side-menu"
            element={
              <ProtectedRoute>
                <SideMenu
                  setBrand={setBrand}
                  setStoreTitle={setStoreTitle}
                  setStoreIds={setStoreIds}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands"
            element={
              <ProtectedRoute>
                <Brands setBrand={setBrand} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-page/:brandId"
            element={
              <ProtectedRoute>
                <BrandPage
                  setBrand={setBrand}
                  setStoreIds={setStoreIds}
                  setStoreTitle={setStoreTitle}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/points-history"
            element={
              <ProtectedRoute>
                <PointsHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-favourites"
            element={
              <ProtectedRoute>
                <MyFavourites
                  setStoreIds={setStoreIds}
                  setStoreImages={setStoreImages}
                  storeImages={storeImages}
                  setStoreTitle={setStoreTitle}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile-update"
            element={
              <ProtectedRoute>
                <ProfileUpdate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tier-information"
            element={
              <ProtectedRoute>
                <TierInformation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/receipt-approval"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gift-card"
            element={
              <ProtectedRoute>
                <GiftCard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer-a-friend"
            element={
              <ProtectedRoute>
                <ReferAFriend />
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-in"
            element={
              <ProtectedRoute>
                <CheckIn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/membership-scan"
            element={
              <ProtectedRoute>
                <MembershipScan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rewards-list"
            element={
              <ProtectedRoute>
                <RewardsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rewards-list/:brandName"
            element={
              <ProtectedRoute>
                <RewardDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands-store-listing"
            element={
              <ProtectedRoute>
                <BrandsStoreListing
                  brand={brand}
                  setStore={setStore}
                  storeIds={storeIds}
                  setStoreImages={setStoreImages}
                  storeImages={storeImages}
                  storeTitle={storeTitle}
                  setStoreTitle={setStoreTitle}
                  setStoreIds={setStoreIds}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store-page"
            element={
              <ProtectedRoute>
                <StorePage store={store} storeImages={storeImages} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rewards-catalog"
            element={
              <ProtectedRoute>
                <RewardsCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/your-offers"
            element={
              <ProtectedRoute>
                <YourOffers
                  setStoreIds={setStoreIds}
                  setStoreImages={setStoreImages}
                  storeImages={storeImages}
                  setStoreTitle={setStoreTitle}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-support"
            element={
              <ProtectedRoute>
                <CustomerSupport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-feedback-form"
            element={
              <ProtectedRoute>
                <CustomerFeedbackForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-games"
            element={
              <ProtectedRoute>
                <MyGames />
              </ProtectedRoute>
            }
          />
          <Route
            path="/game/:gameId"
            element={
              <ProtectedRoute>
                <Game />
              </ProtectedRoute>
            }
          />
        </Routes>
        <BottomNav showNav={showNav} />
        </Suspense>
      </AuthProvider>
    </div>
  );
}

export default App;
